import Table from '../../../components/common/Table'
import Button from '../../../components/common/Button'
import { Article } from './ArticleForm'
import { getImageUrl } from '../../../utils/helpers'

// Type for articles with required ID (for display in table)
type ArticleWithId = Article & { id: number }

interface ArticleTableProps {
  articles: Article[]
  onEdit: (article: Article) => void
  onDelete: (article: Article) => void
}

function ArticleTable({ articles, onEdit, onDelete }: ArticleTableProps) {
  const getStatusBadge = (status: Article['status']) => {
    const badges = {
      draft: { label: 'Brouillon', className: 'badge-draft' },
      published: { label: 'Publié', className: 'badge-published' },
      archived: { label: 'Archivé', className: 'badge-archived' },
    }

    const badge = badges[status]
    return <span className={`status-badge ${badge.className}`}>{badge.label}</span>
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '-'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const columns = [
    {
      key: 'cover_media',
      label: 'Image',
      render: (article: ArticleWithId) =>
        article.cover_media ? (
          <img
            src={getImageUrl(article.cover_media.file_path)}
            alt={article.title}
            className="article-thumbnail"
          />
        ) : (
          <div className="article-thumbnail-placeholder">Pas d'image</div>
        ),
    },
    {
      key: 'title',
      label: 'Titre',
      render: (article: ArticleWithId) => (
        <div className="article-title-cell">
          <div className="article-title">{article.title}</div>
          <div className="article-slug">{article.slug}</div>
        </div>
      ),
    },
    {
      key: 'excerpt',
      label: 'Extrait',
      render: (article: ArticleWithId) => (
        <div className="article-excerpt">{truncateText(article.excerpt, 80)}</div>
      ),
    },
    {
      key: 'status',
      label: 'Statut',
      render: (article: ArticleWithId) => getStatusBadge(article.status),
    },
    {
      key: 'published_at',
      label: 'Date de publication',
      render: (article: ArticleWithId) => formatDate(article.published_at),
    },
    {
      key: 'updated_at',
      label: 'Dernière modification',
      render: (article: ArticleWithId) => formatDate(article.updated_at || ''),
    },
  ]

  const renderActions = (article: ArticleWithId) => (
    <div className="table-action-buttons">
      <Button size="small" variant="outline" onClick={() => onEdit(article)}>
        Modifier
      </Button>
      <Button
        size="small"
        variant="outline"
        onClick={() => {
          if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'article "${article.title}" ?`)) {
            onDelete(article)
          }
        }}
      >
        Supprimer
      </Button>
    </div>
  )

  return (
    <div className="articles-table">
      <Table data={articles as ArticleWithId[]} columns={columns} actions={renderActions} />
    </div>
  )
}

export default ArticleTable
